/* ============================================================================
   Formato (es-MX). Todo el texto numérico de la app pasa por aquí.
   ========================================================================== */

const MONTHS_LONG = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
] as const

const MONTHS_SHORT = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sep',
  'oct',
  'nov',
  'dic',
] as const

/** Periodo mensual en formato `YYYY-MM`. */
export type Period = string

export function isPeriod(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

export function periodParts(period: Period): { year: number; month: number } {
  const year = Number(period.slice(0, 4))
  const month = Number(period.slice(5, 7))
  return { year, month }
}

/** Índice absoluto de meses; sirve para restar periodos. */
export function periodIndex(period: Period): number {
  const { year, month } = periodParts(period)
  return year * 12 + (month - 1)
}

export function periodFromIndex(index: number): Period {
  const year = Math.floor(index / 12)
  const month = (index % 12) + 1
  return `${year}-${String(month).padStart(2, '0')}`
}

export function addMonths(period: Period, delta: number): Period {
  return periodFromIndex(periodIndex(period) + delta)
}

export function monthsBetween(a: Period, b: Period): number {
  return periodIndex(b) - periodIndex(a)
}

/** «mayo de 2026» */
export function formatPeriodLong(period: Period): string {
  const { year, month } = periodParts(period)
  return `${MONTHS_LONG[month - 1]} de ${year}`
}

/** «Mayo 2026» */
export function formatPeriodTitle(period: Period): string {
  const { year, month } = periodParts(period)
  const name = MONTHS_LONG[month - 1] ?? ''
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`
}

/** «may 2026» */
export function formatPeriodShort(period: Period): string {
  const { year, month } = periodParts(period)
  return `${MONTHS_SHORT[month - 1]} ${year}`
}

/** «may» — para ticks de eje donde el año va aparte. */
export function formatMonthOnly(period: Period): string {
  const { month } = periodParts(period)
  return MONTHS_SHORT[month - 1] ?? ''
}

export function periodYear(period: Period): string {
  return period.slice(0, 4)
}

/* --- Números ------------------------------------------------------------- */

const nfCache = new Map<string, Intl.NumberFormat>()

function nf(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options)
  let cached = nfCache.get(key)
  if (!cached) {
    cached = new Intl.NumberFormat('es-MX', options)
    nfCache.set(key, cached)
  }
  return cached
}

/** «$1,234.56» */
export function currency(value: number, decimals = 2): string {
  return nf({
    style: 'currency',
    currency: 'MXN',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)
}

/** «$1,235» — sin centavos, para números grandes. */
export function currency0(value: number): string {
  return currency(value, 0)
}

/** «1,234.5» */
export function number(value: number, decimals = 1): string {
  return nf({ minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value)
}

export function integer(value: number): string {
  return nf({ maximumFractionDigits: 0 }).format(value)
}

/** «12.9 mil», «4.2 M» — para cifras de contexto, no para pesos exactos. */
export function compact(value: number): string {
  return nf({ notation: 'compact', maximumFractionDigits: 1 }).format(value)
}

/** «+3.4 %» / «−1.2 %» con signo tipográfico (menos real, no guion). */
export function percentDelta(value: number, decimals = 1): string {
  const rounded = Number(value.toFixed(decimals))
  if (Object.is(rounded, 0) || rounded === 0) return `0.0 %`
  const sign = rounded > 0 ? '+' : '−'
  return `${sign}${number(Math.abs(rounded), decimals)} %`
}

/** «3.4 %» sin signo. */
export function percent(value: number, decimals = 1): string {
  return `${number(value, decimals)} %`
}

/** «+$42.10» / «−$8.30» */
export function currencyDelta(value: number, decimals = 2): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''
  return `${sign}${currency(Math.abs(value), decimals)}`
}

/** Flecha del delta. Redundante con el signo: es el canal no-color. */
export function deltaArrow(value: number): string {
  if (value > 0) return '▲'
  if (value < 0) return '▼'
  return '–'
}

/**
 * Dirección semántica de un cambio de precio: subir es adverso para la
 * seguridad alimentaria. Devuelve el token de color a usar.
 */
export type DeltaTone = 'adverse' | 'favorable' | 'neutral'

export function priceTone(delta: number, threshold = 0.05): DeltaTone {
  if (delta > threshold) return 'adverse'
  if (delta < -threshold) return 'favorable'
  return 'neutral'
}

/* --- Texto --------------------------------------------------------------- */

export function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural
}

/** «12 de julio de 2026» a partir de un ISO date. */
export function formatDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getUTCDate()} de ${MONTHS_LONG[d.getUTCMonth()]} de ${d.getUTCFullYear()}`
}

/** Redondea a ticks «limpios» para un eje. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return [min]
  const span = max - min
  const rawStep = span / Math.max(1, count)
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * magnitude)
  const step = candidates.find((c) => c >= rawStep) ?? candidates[candidates.length - 1]!

  /*
     Guardia contra el bucle infinito: con un rango absurdamente pequeño,
     `magnitude` se va a cero por subdesbordamiento y `step` queda en 0, así que
     el `for` de abajo nunca avanzaría. Un eje colgado deja la app congelada, no
     sólo fea.
  */
  if (!(step > 0)) return [min, max]

  const start = Math.ceil(min / step) * step
  const ticks: number[] = []
  // Tope defensivo: `count` es una sugerencia, no un límite duro del bucle.
  const limit = Math.max(count * 4, 12)
  for (let v = start; v <= max + step * 1e-6 && ticks.length < limit; v += step) {
    ticks.push(Number(v.toFixed(10)))
  }
  return ticks
}

/**
 * Acorta un nombre para la etiqueta al final de una curva.
 *
 * Esa etiqueta vive DENTRO del SVG, con un margen reservado fijo, así que un
 * nombre largo se corta a media palabra y queda ilegible. Aquí se corta por
 * palabra completa y sólo cuando hace falta. El nombre íntegro sigue viajando en
 * `label`, que es lo que leen la leyenda, el tooltip y la tabla espejo, donde no
 * hay límite de ancho y recortar sí escondería información.
 */
export function shortenLabel(name: string, max = 20): string {
  if (name.length <= max) return name
  const words = name.split(' ')
  let out = words[0] ?? name
  for (const word of words.slice(1)) {
    if (`${out} ${word}`.length > max) break
    out = `${out} ${word}`
  }
  return out === name ? name : `${out}…`
}
